// Tiny sync-status indicator. Three states:
//   • green  — all changes saved
//   • amber  — saving (pulse animation)
//   • red    — offline, working locally
//
// Renders an 8px circle and a visually-hidden aria-live label so screen
// readers announce status changes. A `title` attribute on the dot supplies
// the hover tooltip — no popper, no extra DOM.

import "./sync-dot.css";

import type { SyncStatus, SyncStatusController } from "../../offline/status";

export interface SyncDotOptions {
  status: SyncStatusController;
  /** Optional tooltip override per status. */
  labels?: Partial<Record<SyncStatus, string>>;
}

export interface SyncDotHandle {
  el: HTMLElement;
  destroy: () => void;
}

const DEFAULT_LABELS: Record<SyncStatus, string> = {
  synced: "All changes saved",
  syncing: "Saving changes…",
  offline: "Working offline — changes will sync when reconnected",
};

export function createSyncDot(opts: SyncDotOptions): SyncDotHandle {
  const labels: Record<SyncStatus, string> = {
    ...DEFAULT_LABELS,
    ...(opts.labels ?? {}),
  };

  const el = document.createElement("span");
  el.className = "tkos-sync-dot";

  const dot = document.createElement("span");
  dot.className = "tkos-sync-dot__dot";
  dot.setAttribute("aria-hidden", "true");

  const sr = document.createElement("span");
  sr.className = "tkos-sync-dot__sr";
  sr.setAttribute("aria-live", "polite");
  sr.setAttribute("role", "status");

  el.appendChild(dot);
  el.appendChild(sr);

  function render(state: SyncStatus): void {
    el.dataset["status"] = state;
    const label = labels[state];
    el.setAttribute("title", label);
    sr.textContent = `Status: ${label}`;
  }

  render(opts.status.get());
  const off = opts.status.on("change", (s) => render(s));

  return {
    el,
    destroy(): void {
      off();
      el.remove();
    },
  };
}
